import {Box, Tab, Tabs, Typography, useMediaQuery} from '@mui/material';
import './ProtocolSection.scss';
import {useCallback, useState, useMemo, useEffect, useRef} from 'react';
import IssueCodesTab from '../../common/issue-codes-tab/IssueCodesTab';
import {t} from 'i18next';
import ProtocolCaseContext from '../../common/casecontextv2/ProtocolCaseContext';
import ProgressLoader from '../../../../ingestion/ingestion-apo/progress-loader/ProgressLoader';
import UploadProtocol from './upload-protocol/UploadProtocol';
import RelevanceCriteria from '../../common/relevance-criteria/RelevanceCriteria';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {
  ProtocolStatus,
  ProtocolVersionSummary,
  setProtocolData,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {ISelectItem} from '../../../../../shared/types';
import {useGetUploadedDataById} from '../../../../../hooks/ai/review/useReview';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../shared/constants';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import {
  getInAppNavigationPath,
  isLeaveGuardExemptClick,
  navigateToPendingOrProtocolTab,
} from '../../common/protocolIntelligenceV2/header/exit-protocol-modal/protocolExitNavigation';
import {useNavigate} from 'react-router-dom';
import {getProtocolFileDisplayName} from '../utils/protocolVersionPayload';
import {
  isProtocolUploadCompletedStatus,
  isProtocolUploadFailedStatus,
} from '../../../../../api/types/aiReview.type';

interface TabData {
  label: string;
  disabled: boolean;
  content: React.ReactNode;
}

export interface ProtocolSectionProps {
  /** When approved, protocol is view-only and upload banner is replaced by version dropdown. */
  status?: ProtocolStatus;
  versions?: ProtocolVersionSummary[];
  selectedVersionId?: number;
  onVersionChange?: (versionId: number) => void;
  /** Increment to focus the Case Context tab after a history selection. */
  focusCaseContextRequest?: number;
  /** When false, hide version dropdown (e.g. draft V1). Default true when versions exist. */
  showVersionControls?: boolean;
  /** Disable version dropdown while an unsaved revision is active. */
  isVersionDropdownDisabled?: boolean;
  isUploadingProtocol?: boolean;
  setIsUploadingProtocol?: React.Dispatch<React.SetStateAction<boolean>>;
}
type ToastConfigType = {
  show?: boolean;
  title?: string;
  message?: string;
  type?: ToastType;
};

const DEFAULT_APPROVED_VERSIONS: ProtocolVersionSummary[] = [
  {versionId: 1, name: 'v1', status: 'approved'},
];

const clickableSelector = [
  'button',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[onclick]',
  '.dropdowns',
  'li',
].join(',');

export const ProtocolSection = ({
  status = 'draft',
  versions: versionsProp,
  selectedVersionId: selectedVersionIdProp,
  onVersionChange,
  focusCaseContextRequest,
  showVersionControls = true,
  isVersionDropdownDisabled = false,
  isUploadingProtocol,
  setIsUploadingProtocol,
}: ProtocolSectionProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const [progressPercent, setProgressPercent] = useState(0);
  const [protocolId, setProtocolId] = useState<number | null>(null);
  const [uploadedDocId, setUploadedDocId] = useState<number | null>(null);
  const uploadedFileNameRef = useRef<string>('');
  const lastAppliedUploadArtifactIdRef = useRef<number | null>(null);
  const [toastMsgConfig, setToastMsgConfig] = useState<ToastConfigType>({
    show: false,
  });
  const [showLeaveProtocolModal, setShowLeaveProtocolModal] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const existingUploadedFileName = useSelector(
    (state: RootState) => state.aiReviewV2.protocol.fileDetails?.fileName
  );
  const persistedProtocolVersionId = useSelector(
    (state: RootState) => state.aiReviewV2.protocol.protocolVersionId
  );
  const isApproved = status?.trim().toLowerCase() === 'approved';

  useEffect(() => {
    if (focusCaseContextRequest == null) {
      return;
    }
    setActiveTab(0);
  }, [focusCaseContextRequest]);

  const versions = useMemo(() => {
    if (!showVersionControls) {
      return [];
    }
    if (versionsProp?.length) {
      return versionsProp;
    }
    return isApproved ? DEFAULT_APPROVED_VERSIONS : [];
  }, [isApproved, showVersionControls, versionsProp]);

  const hasVersions = showVersionControls && versions.length > 0;

  const versionDropdownItems: ISelectItem[] = useMemo(
    () =>
      versions.map((version) => ({
        id: version.versionId,
        label: t('ai.review.protocolIntelligence.versionLabel', {
          version: version.name,
        }),
      })),
    [versions]
  );

  const [internalVersionId, setInternalVersionId] = useState<number>(
    selectedVersionIdProp ?? versions[0]?.versionId ?? 1
  );
  const [versionDropdownResetKey, setVersionDropdownResetKey] = useState(0);

  const selectedVersionId = selectedVersionIdProp ?? internalVersionId;

  const selectedVersion =
    versionDropdownItems.find((item) => item.id === selectedVersionId) ??
    versionDropdownItems[0] ??
    null;

  /** Artifact id from SignalR `Completed` — details API runs only after this is set. */
  const uploadFetchArtifactId = protocolId;

  const {data: protocolFromFile, isLoading: protocolDataLoading} =
    useGetUploadedDataById(uploadFetchArtifactId);
  const isProtocolProcessing = Boolean(
    isUploadingProtocol || protocolDataLoading
  );
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const tabData: TabData[] = [
    {
      label: t('ai.review.reviewAndApprove.caseContext'),
      disabled: false,
      content: <ProtocolCaseContext mode={isApproved ? 'view' : 'edit'} />,
    },
    {
      label: t('ai.review.reviewAndApprove.relevanceCriteria'),
      disabled: false,
      content: <RelevanceCriteria mode={isApproved ? 'view' : 'edit'} />,
    },
    {
      label: t('ai.review.reviewAndApprove.issueCodes'),
      disabled: false,
      content: <IssueCodesTab mode={isApproved ? 'view' : 'edit'} />,
    },
  ];
  const activeTabData = tabData.find((_, index) => index === activeTab);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleVersionChange = (value: ISelectItem | null) => {
    if (value?.id == null) {
      return;
    }
    const nextVersionId = Number(value.id);
    if (onVersionChange) {
      setVersionDropdownResetKey((key) => key + 1);
    } else {
      setInternalVersionId(nextVersionId);
    }
    onVersionChange?.(nextVersionId);
  };

  const a11yProps = (index: number) => ({
    id: `protocol-tab-${index}`,
    'aria-controls': `protocol-tabpanel-${index}`,
  });

  const handleUploadProtocol = useCallback(
    (isUploading: boolean, docId?: number | null, fileName?: string | null) => {
      const resolvedFileName = getProtocolFileDisplayName(fileName);
      if (!isUploading && docId == null && !resolvedFileName) {
        uploadedFileNameRef.current = '';
        setUploadedDocId(null);
        setProtocolId(null);
        lastAppliedUploadArtifactIdRef.current = null;
        setIsUploadingProtocol?.(false);
        return;
      }
      if (resolvedFileName) {
        uploadedFileNameRef.current = resolvedFileName;
      }
      setIsUploadingProtocol?.(isUploading);
      setUploadedDocId(docId ?? null);
    },
    [setIsUploadingProtocol]
  );

  const targetPath = useRef<string | null>(null);
  const isProtocolProcessingRef = useRef(isProtocolProcessing);
  const onLeaveGuardEventRef = useRef<
    (event: MouseEvent | KeyboardEvent) => void
  >(() => undefined);

  isProtocolProcessingRef.current = isProtocolProcessing;

  // Stable document listener so add/remove always target the same function.
  const leaveGuardListener = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      onLeaveGuardEventRef.current(event);
    },
    []
  );

  const manageEvent = useCallback(
    (isAdding: boolean) => {
      if (isAdding) {
        document.addEventListener('click', leaveGuardListener, {capture: true});
        document.addEventListener('keydown', leaveGuardListener, {
          capture: true,
        });
      } else {
        document.removeEventListener('click', leaveGuardListener, {
          capture: true,
        });
        document.removeEventListener('keydown', leaveGuardListener, {
          capture: true,
        });
      }
    },
    [leaveGuardListener]
  );

  onLeaveGuardEventRef.current = (event: MouseEvent | KeyboardEvent) => {
    // Never show leave modal once preprocessing has completed.
    if (!isProtocolProcessingRef.current) {
      return;
    }
    if ('key' in event && event.key !== ' ' && event.key !== 'Enter') {
      return;
    }
    const target = event?.target as HTMLElement;
    if (isLeaveGuardExemptClick(target)) {
      return;
    }
    const isClickedInsideSubGroup = Boolean(
      target?.closest('.protocol-section-container')
    );
    if (isClickedInsideSubGroup) {
      return;
    }
    const clickableElement = target?.closest(clickableSelector);

    if (clickableElement) {
      const navigationPath = getInAppNavigationPath(target);
      targetPath.current = navigationPath;
      setShowLeaveProtocolModal(true);
      event.preventDefault();
      event.stopPropagation();
      manageEvent(false);
    }
  };

  useEffect(() => {
    if (!isProtocolProcessing) {
      setShowLeaveProtocolModal(false);
      manageEvent(false);
      return;
    }

    manageEvent(true);
    return () => {
      manageEvent(false);
    };
  }, [isProtocolProcessing, manageEvent]);

  useEffect(() => {
    if (!isProtocolProcessing) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isProtocolProcessing]);

  useEffect(() => {
    if (!uploadedDocId || !lastEvent) return;

    const {payload: protocolDetails, type} = lastEvent.payload;
    if (
      type === 'ManageProtocolStatusUpdated' &&
      protocolDetails.artifactId === uploadedDocId
    ) {
      setProgressPercent(protocolDetails?.uploadProgress);
      if (isProtocolUploadCompletedStatus(protocolDetails?.aiStatus)) {
        setProtocolId(protocolDetails?.artifactId);
        setIsUploadingProtocol?.(false);
        manageEvent(false);
      } else if (isProtocolUploadFailedStatus(protocolDetails?.aiStatus)) {
        setIsUploadingProtocol?.(false);
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message: t('ai.review.protocolIntelligence.uploadErrorDescription'),
          title: t('common.error'),
        });
        manageEvent(false);
      }
    }
  }, [lastEvent, uploadedDocId]);

  useEffect(() => {
    if (!protocolFromFile || protocolId == null) {
      return;
    }

    if (lastAppliedUploadArtifactIdRef.current === protocolId) {
      return;
    }

    // Saved draft: hydrate from upload API only during an in-session re-upload.
    if (persistedProtocolVersionId != null && uploadedDocId == null) {
      return;
    }

    const uploadedFileName =
      getProtocolFileDisplayName(protocolFromFile.fileDetails?.fileName) ||
      getProtocolFileDisplayName(uploadedFileNameRef.current) ||
      getProtocolFileDisplayName(existingUploadedFileName);
    if (uploadedFileName) {
      uploadedFileNameRef.current = uploadedFileName;
    }
    const uploadedArtifactId =
      protocolId ??
      (protocolFromFile.fileDetails?.reviewProtocolDocumentID != null &&
      protocolFromFile.fileDetails.reviewProtocolDocumentID !== ''
        ? Number(protocolFromFile.fileDetails.reviewProtocolDocumentID)
        : null);

    dispatch(
      setProtocolData({
        caseContext: protocolFromFile.caseContext,
        relevanceCriteria: protocolFromFile.relevanceCriteria,
        issueCoding: protocolFromFile.issueCoding,
        ...(uploadedFileName || uploadedArtifactId != null
          ? {
              fileDetails: {
                fileName: uploadedFileName,
                artifactId: Number.isFinite(uploadedArtifactId)
                  ? uploadedArtifactId
                  : null,
              },
            }
          : {}),
      })
    );
    lastAppliedUploadArtifactIdRef.current = protocolId;
    setToastMsgConfig({
      show: true,
      type: ToastType.Success,
      message: t('ai.review.protocolIntelligence.uploadSuccessDescription'),
      title: t('common.success'),
    });
  }, [
    dispatch,
    existingUploadedFileName,
    persistedProtocolVersionId,
    protocolFromFile,
    protocolId,
    uploadedDocId,
  ]);

  const handleLeavePage = () => {
    navigateToPendingOrProtocolTab(navigate, targetPath.current);
    setShowLeaveProtocolModal(false);
  };
  const handleStayOnPage = () => {
    targetPath.current = null;
    setShowLeaveProtocolModal(false);
    // Only restore leave-guard while preprocessing is still in progress.
    if (isProtocolProcessingRef.current) {
      manageEvent(true);
    }
  };

  // ≤1200px: upload banner is its own card above the tabs so labels are not clipped.
  // Wider screens keep the banner on the tabs row. Version dropdown always stays there.
  const isNarrowViewport = useMediaQuery('(max-width:1200px)');

  const versionDropdown = hasVersions ? (
    <Box className="protocol-header-aside protocol-header-aside--version">
      <Box className="protocol-version-dropdown">
        <ArrSingleSelectDropdown
          className="protocol-version-select"
          key={`protocol-version-dropdown-${versionDropdownResetKey}`}
          dataTestId="protocol-version-dropdown"
          dropdownLabel={t(
            'ai.review.protocolIntelligence.versionDropdownLabel'
          )}
          dropdownItems={versionDropdownItems}
          dropdownItemKey="id"
          dropdownItemDisplay="label"
          selectedValues={selectedVersion}
          onSelectionChange={handleVersionChange}
          sizeVariant="medium"
          searchable={false}
          disabled={isVersionDropdownDisabled}
          selectRestProps={{
            'aria-label': t(
              'ai.review.protocolIntelligence.versionDropdownLabel'
            ),
            'aria-haspopup': 'listbox',
          }}
        />
      </Box>
    </Box>
  ) : null;

  const uploadBanner = !hasVersions ? (
    <Box className="protocol-header-aside protocol-header-aside--upload">
      <UploadProtocol
        handleUploadProtocol={handleUploadProtocol}
        isBtnDisabled={isUploadingProtocol ?? false}
      />
    </Box>
  ) : null;

  const shouldStackUploadBanner = isNarrowViewport && Boolean(uploadBanner);

  const tabs = (
    <Tabs
      value={activeTab}
      onChange={handleChange}
      className="protocol-tabs"
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      aria-label={t('ai.review.protocolIntelligence.protocolSectionsLabel')}
    >
      {tabData.map((tab, index) => (
        <Tab
          key={tab.label}
          label={tab.label}
          className="protocol-tab"
          {...a11yProps(index)}
        />
      ))}
    </Tabs>
  );

  return (
    <Box
      className={`protocol-section-container${
        shouldStackUploadBanner ? ' protocol-section-container--stacked' : ''
      }${
        isProtocolProcessing ? ' protocol-section-container--processing' : ''
      }`}
      data-testid="protocol-section"
    >
      {shouldStackUploadBanner && uploadBanner}
      <Box className="protocol-section-main">
        <Box className="protocol-header-tabs">
          {tabs}
          {versionDropdown}
          {!shouldStackUploadBanner && uploadBanner}
        </Box>
        <Box className="protocol-content">
          <Box
            className={`content-panel typo-text body3_size${
              activeTab === 0 ? ' content-panel--case-context' : ''
            }${activeTab === 1 ? ' content-panel--relevance-criteria' : ''}`}
            role="tabpanel"
            id={`protocol-tabpanel-${activeTab}`}
            aria-labelledby={`protocol-tab-${activeTab}`}
            aria-busy={Boolean(isUploadingProtocol || protocolDataLoading)}
            data-testid={`protocol-tabpanel-${activeTab}`}
          >
            {isUploadingProtocol || protocolDataLoading ? (
              <Box
                className="progress-bar"
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-testid="protocol-processing-status"
              >
                <ProgressLoader
                  progressPercent={protocolDataLoading ? 100 : progressPercent}
                  showProgressbarOnly={true}
                  ariaLabel={t(
                    'ai.review.protocolIntelligence.processingProtocolDocument'
                  )}
                />
                <Typography className="typo-text subhead4_size">
                  {t(
                    'ai.review.protocolIntelligence.processingProtocolDocument'
                  )}
                </Typography>
              </Box>
            ) : (
              activeTabData?.content
            )}
          </Box>
        </Box>
      </Box>
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
      {showLeaveProtocolModal && (
        <ArrModal
          className="leave-page-modal"
          showClose={false}
          showModal={showLeaveProtocolModal}
          onClose={handleStayOnPage}
          title={t(
            'ai.review.protocolIntelligence.leaveProtocolModalDetails.title'
          )}
          aria-label={t(
            'ai.review.protocolIntelligence.leaveProtocolModalDetails.title'
          )}
          dataTestId="arr-leave-page-modal"
          buttons={[
            {
              id: t('common.leavePage'),
              label: t('common.leavePage'),
              onClick: handleLeavePage,
              variant: 'secondary',
              dataTestId: 'modal-no',
            },
            {
              id: t('common.stayOnPage'),
              label: t('common.stayOnPage'),
              onClick: handleStayOnPage,
              variant: 'primary',
              dataTestId: 'modal-yes',
            },
          ]}
        >
          <Typography
            className="typo-text body2_size text-body-strong"
            data-testid={`leave-page-description`}
          >
            {t(
              'ai.review.protocolIntelligence.leaveProtocolModalDetails.description'
            )}
          </Typography>
        </ArrModal>
      )}
    </Box>
  );
};

export default ProtocolSection;
